import React, { useMemo } from 'react';

import { ChipsList } from '@/components/ChipsList';
import { isWebApplication } from '@/hooks/useAssets';
import { getPrioritizedAttackSurface } from '@/sections/asset/components/cells/attackSurfaceCell.util';
import { Asset } from '@/types';
import { formatAttackSurfaceLabel } from '@/utils/mappings.util';

interface AttackSurfaceComponentProps {
  asset?: Asset;
  attackSurface?: string[];
  maxVisible?: number;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
  wrapInTooltip?: boolean; // Wrap entire cell in tooltip for better UX
  activeFilters?: string[]; // Active attack surface filters for prioritization
}

/**
 * Simple capitalize function for attack surface values
 */
const capitalize = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Determines the attack surface values to display for an asset
 */
const getAttackSurfaceValues = (asset: Asset): string[] => {
  // For WebApplications, use the attackSurface field
  if (isWebApplication(asset)) {
    return asset.attackSurface && asset.attackSurface.length > 0
      ? asset.attackSurface.map(s => capitalize(s)).sort()
      : ['External'];
  }

  // Use attackSurface if available
  if (asset.attackSurface && asset.attackSurface.length > 0) {
    return asset.attackSurface.map(s => capitalize(s)).sort();
  }

  // Use private field
  if (asset.private === true) {
    return ['Internal'];
  }

  // Fallback logic for assets without surface data
  const isInternal = asset.key.startsWith('#addomain');
  return isInternal ? ['Internal'] : ['External'];
};

/**
 * Centralized component for displaying attack surface information
 * Can accept either an asset object or a direct attackSurface array
 *
 * Features:
 * - Configurable maxVisible for chip display (1 for tables, Infinity for full display)
 * - Optional wrapInTooltip for cell-wide hover behavior
 * - Automatic sorting and formatting of attack surface values
 * - Filter-based prioritization: Filtered values appear first (alphabetically)
 */
export const AttackSurfaceComponent: React.FC<AttackSurfaceComponentProps> = ({
  asset,
  attackSurface,
  maxVisible = Infinity,
  size = 'xs',
  className = 'max-w-[160px] min-w-[160px]',
  wrapInTooltip = false,
  activeFilters,
}) => {
  // Determine what values to display and prioritize based on filters
  const displayValues = useMemo(() => {
    let values: string[] = [];

    if (asset) {
      // When given an asset, compute its attack surface
      values = asset.attackSurface?.slice() || getAttackSurfaceValues(asset);
    } else if (attackSurface) {
      // When given a direct array, format and display it
      values = attackSurface.map(formatAttackSurfaceLabel);
    }

    // Apply filter-based prioritization
    return getPrioritizedAttackSurface(values, activeFilters);
  }, [asset, attackSurface, activeFilters]);

  return (
    <ChipsList
      items={displayValues}
      maxVisible={maxVisible}
      variant="default"
      className={className}
      size={size}
      data-testid="attack-surface-chips"
      wrapInTooltip={wrapInTooltip}
    />
  );
};
